import React from "react";
import { Card, Table, Alert, Space, Typography, Tag } from "antd";
import { TrophyOutlined } from "@ant-design/icons";

const { Text } = Typography;

const RobustRankingsTab = ({
  calculateBestOptions,
  expectedDays,
  expectedMove,
  setSelectedStrike,
  selectedStrike,
}) => {
  if (!calculateBestOptions) return null;

  const columns = [
    {
      title: "Rank",
      key: "rank",
      render: (_, __, index) => {
        const colors = ["#faad14", "#d9d9d9", "#cd7f32"];
        return (
          <Space>
            <Text
              strong
              style={{
                fontSize: 16,
                color: colors[index] || "#000",
              }}
            >
              #{index + 1}
            </Text>
            {index < 3 && <TrophyOutlined style={{ color: colors[index] }} />}
          </Space>
        );
      },
      width: 100,
      fixed: "left",
    },
    {
      title: "Strike",
      dataIndex: "strike",
      key: "strike",
      render: (val) => <Text strong>${val}</Text>,
      width: 100,
      fixed: "left",
    },
    {
      title: "Robust Score",
      dataIndex: "score",
      key: "score",
      render: (val) => <Tag color="blue">{val.toFixed(2)}</Tag>,
      width: 120,
    },
    {
      title: "Perfect Return",
      dataIndex: "returnPct",
      key: "returnPct",
      render: (val) => <Tag color="green">{val.toFixed(2)}%</Tag>,
      width: 130,
    },
    {
      title: "Avg Return",
      dataIndex: "avgReturn",
      key: "avgReturn",
      render: (val) => `${val.toFixed(2)}%`,
      width: 120,
    },
    {
      title: "Worst Case",
      dataIndex: "minReturn",
      key: "minReturn",
      render: (val) => (
        <Text
          strong
          style={{
            color: val > 0 ? "#52c41a" : "#ff4d4f",
          }}
        >
          {val.toFixed(2)}%
        </Text>
      ),
      sorter: (a, b) => a.minReturn - b.minReturn,
      width: 120,
    },
    {
      title: "Reliability",
      dataIndex: "profitableRatio",
      key: "profitableRatio",
      render: (val, record) => (
        <div>
          <Tag
            color={val >= 0.8 ? "green" : val >= 0.6 ? "orange" : "red"}
          >
            {(val * 100).toFixed(0)}%
          </Tag>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.profitableScenarios}/{record.scenariosTested}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.profitableRatio - b.profitableRatio,
      width: 120,
    },
    {
      title: "Entry Cost",
      dataIndex: "entryPrice",
      key: "entryPrice",
      render: (val) => `$${val.toFixed(2)}`,
      width: 120,
    },
    {
      title: "Perfect Profit",
      dataIndex: "priceChange",
      key: "priceChange",
      render: (val) => (
        <Text strong style={{ color: "#52c41a" }}>
          +${val.toFixed(2)}
        </Text>
      ),
      width: 130,
    },
    {
      title: "Worst Profit",
      dataIndex: "worstCaseProfit",
      key: "worstCaseProfit",
      render: (val) => (
        <Text
          strong
          style={{
            color: val > 0 ? "#52c41a" : "#ff4d4f",
          }}
        >
          {val > 0 ? "+" : ""}${val.toFixed(2)}
        </Text>
      ),
      width: 120,
    },
  ];

  return (
    <Card
      title={
        <Space>
          <TrophyOutlined />
          <Text strong>🛡️ Robust Strike Rankings (Handles Signal Misses)</Text>
        </Space>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 13 }}>
          Ranked by performance across {expectedDays - 2} to {expectedDays + 2}{" "}
          days, {expectedMove - 2}% to {expectedMove + 2}% moves
        </Text>
      }
    >
      <Alert
        message="Understanding Robust Rankings"
        description={
          <div>
            <p>
              <strong>What makes a strike "robust"?</strong> A robust strike
              stays profitable even when things don't go perfectly:
            </p>
            <ul>
              <li>
                <strong>Avg Return:</strong> Average performance across all
                tested scenarios
              </li>
              <li>
                <strong>Worst Case:</strong> Minimum return if signal misses
                (timing or % off)
              </li>
              <li>
                <strong>Reliability:</strong> What % of scenarios stay
                profitable (
                <Tag color="green" style={{ margin: "0 4px" }}>
                  80%+
                </Tag>
                = excellent,
                <Tag color="orange" style={{ margin: "0 4px" }}>
                  60-80%
                </Tag>
                = good,
                <Tag color="red" style={{ margin: "0 4px" }}>
                  &lt;60%
                </Tag>
                = risky)
              </li>
              <li>
                <strong>Worst Profit:</strong> Actual $ profit in the worst
                tested scenario
              </li>
            </ul>
            <p>
              <strong>Top ranked strike</strong> might not have the highest
              "Perfect Return", but has the best balance of upside potential and
              downside protection.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Table
        dataSource={calculateBestOptions}
        scroll={{ x: 1400 }}
        pagination={false}
        rowKey="strike"
        size="middle"
        columns={columns}
        onRow={(record) => ({
          onClick: () => setSelectedStrike(record.strike),
          style: {
            cursor: "pointer",
            background: record.strike === selectedStrike ? "#26324b" : undefined,
          },
        })}
      />
    </Card>
  );
};

export default RobustRankingsTab;
